'use client';

import { useQuery } from '@tanstack/react-query';
import type { CompanyNetworkResponse } from '@/lib/backend-api';
import { validateCompanyNetwork } from '@/lib/ssr-schemas';

const COMPANY_NETWORK_KEY = ['company-network'] as const;

async function fetchCompanyNetwork(euId: string, maxNodes: number): Promise<CompanyNetworkResponse | null> {
  const res = await fetch(`/api/companies/${encodeURIComponent(euId)}/network?max_nodes=${maxNodes}`);
  if (!res.ok) throw new Error(`/api/companies/${euId}/network returned ${res.status}`);
  const json: unknown = await res.json();
  const data = json && typeof json === 'object' && 'data' in json ? json.data : null;
  return validateCompanyNetwork(data, { euId });
}

/**
 * The company network graph, starting from the SSR payload. Refetches when
 * `large` asks for the bigger graph (fullscreen: 100 nodes instead of the SSR
 * page's 30) or when `reloadToken` moves past 0 (the backend finished a
 * Gesellschafterliste parse, so the graph may have gained shareholders).
 * Until a refetch lands — or if it fails — the SSR graph stays.
 */
export function useCompanyNetwork(
  euId: string,
  initial: CompanyNetworkResponse,
  { large, reloadToken }: { large: boolean; reloadToken: number },
): CompanyNetworkResponse {
  const maxNodes = large ? 100 : 30;
  const { data } = useQuery({
    queryKey: [...COMPANY_NETWORK_KEY, euId, maxNodes, reloadToken] as const,
    queryFn: () => fetchCompanyNetwork(euId, maxNodes),
    enabled: large || reloadToken > 0,
    staleTime: 5 * 60_000,
    placeholderData: (prev) => prev,
  });
  return data ?? initial;
}
